import * as fs from 'node:fs';
import * as path from 'node:path';
import { CLI_PERMISSION_MODE_DANGEROUS, getCliPermissionModeFromAgentConfig } from '@buildautomaton/types';
import { resolveSessionParentPathForAgentProcess } from '../../paths/session-layout-paths.js';
import type { AcpClientHandle } from './clients/acp-client.js';
import type { LogFn } from '../../log.js';
import { errorMessage } from '../../error-message.js';
import { resolveAgentCommand } from './resolve-agent-command.js';
import { buildAcpSessionBridgeHooks } from './hooks/build-acp-session-bridge-hooks.js';
import { readLocalAgentSessionFile, writeLocalAgentSessionFile } from './local-agent-session-file.js';
import type { AcpAgentKey } from './acp-agent.js';
import { computeAcpAgentKey } from './acp-agent.js';
import { isCliImmediateShutdownRequested } from '../../runtime/cli-process-interrupt.js';
import { buildAcpMcpServers } from '../../mcp/build-acp-mcp-servers.js';

export interface AcpClientState {
  acpHandle: AcpClientHandle | null;
  acpStartPromise: Promise<AcpClientHandle | null> | null;
  lastAcpStartError: string | null;
  /** Last session parent path used when starting the ACP client (restart if it changes). */
  lastAcpCwd: string | null;
  /** Stable key for label + full spawn argv (including mode flags); restart when agent type or mode changes. */
  acpAgentKey: string | null;
  /**
   * Latest ACP `configOptions` for the current agent subprocess (memory-first for permission / mode UX).
   * Cleared when the handle is torn down; seeded from disk when spawning a new client when a session file exists.
   */
  activeSessionConfigOptions: unknown[] | null;
  /**
   * Latest per-prompt `agent_config` from the bridge. Bridge hooks read this on each `session/request_permission`
   * so CLI dangerous mode applies even when the ACP subprocess was started on an earlier prompt.
   */
  latestAgentConfigForBridgeHooks: Record<string, unknown> | null;
  /** Bumped when the client is torn down so in-flight spawn promises are ignored. */
  clientEpoch: number;
}

export function invalidateAcpClientState(state: AcpClientState): void {
  state.clientEpoch += 1;
  state.acpHandle = null;
  state.acpStartPromise = null;
  state.acpAgentKey = null;
  state.activeSessionConfigOptions = null;
  state.latestAgentConfigForBridgeHooks = null;
}

export function createEmptyAcpClientState(): AcpClientState {
  return {
    acpHandle: null,
    acpStartPromise: null,
    lastAcpStartError: null,
    lastAcpCwd: null,
    acpAgentKey: null,
    activeSessionConfigOptions: null,
    latestAgentConfigForBridgeHooks: null,
    clientEpoch: 0,
  };
}

export interface EnsureAcpClientOptions {
  state: AcpClientState;
  acpAgentKey: AcpAgentKey;
  preferredAgentType: string | null;
  mode?: string;
  /** Per-turn agent options (Claude Code reads `claude_permission_mode`). */
  agentConfig?: Record<string, unknown> | null;
  /** Session parent path for the agent process (bridge root or isolated checkout). */
  sessionParentPath?: string;
  /** Resolve the active prompt turn for this shared agent handle when ACP callbacks fire. */
  resolveRouting: () => { sessionId?: string; runId?: string } | undefined;
  /** Cloud session id for ~/.buildautomaton/agent-sessions/<id>.json (ACP resume / config cache). */
  cloudSessionId?: string;
  /** Loopback bridge access server port (passed to the shared MCP server subprocess). */
  bridgeAccessPort?: number | null;
  /** Report latest agent `configOptions` to the bridge DO (model picker in the web app). */
  reportAgentCapabilities?: (info: { agentType: string; configOptions: unknown[] }) => void;
  sendSessionUpdate: (payload: unknown) => void;
  sendRequest: (payload: unknown) => void;
  log: LogFn;
}

/**
 * Ensure the ACP client for this session+agent subprocess is running and return its handle.
 * Prompt turns resolve session/run routing at callback time.
 */
export async function ensureAcpClient(options: EnsureAcpClientOptions): Promise<AcpClientHandle | null> {
  const {
    state,
    acpAgentKey,
    preferredAgentType,
    mode,
    agentConfig,
    sessionParentPath,
    resolveRouting,
    cloudSessionId,
    bridgeAccessPort,
    reportAgentCapabilities,
    sendSessionUpdate,
    sendRequest,
    log,
  } = options;

  const mcpServers = buildAcpMcpServers(bridgeAccessPort ?? 0);

  state.latestAgentConfigForBridgeHooks =
    agentConfig != null && typeof agentConfig === 'object' && !Array.isArray(agentConfig) ? agentConfig : null;

  const targetSessionParentPath = resolveSessionParentPathForAgentProcess(sessionParentPath);

  if (state.acpStartPromise && !state.acpHandle) {
    await state.acpStartPromise;
  }

  if (
    state.acpHandle &&
    state.lastAcpCwd != null &&
    path.resolve(state.lastAcpCwd) !== path.resolve(targetSessionParentPath)
  ) {
    try {
      state.acpHandle.disconnect();
    } catch {
      /* ignore */
    }
    invalidateAcpClientState(state);
  }

  const resolved = resolveAgentCommand(preferredAgentType);
  if (!resolved) {
    log(
      `[Agent] No local agent type (${preferredAgentType === null ? 'none' : `"${preferredAgentType}"`}). Send agent type on prompts or agent configuration after identify.`,
    );
    state.lastAcpStartError =
      'No agent type: ensure the app sends agentType on prompts or agent_config for this bridge.';
    return null;
  }

  const computedAcpAgentKey = computeAcpAgentKey(preferredAgentType, mode, agentConfig);
  if (computedAcpAgentKey !== acpAgentKey) {
    state.lastAcpStartError = 'AcpAgent key mismatch for prompt';
    log(`[Agent] ${state.lastAcpStartError}`);
    return null;
  }

  if (state.acpHandle && state.acpAgentKey !== acpAgentKey) {
    try {
      state.acpHandle.disconnect();
    } catch {
      /* ignore */
    }
    invalidateAcpClientState(state);
  }

  if (state.acpHandle) return state.acpHandle;

  if (!state.acpStartPromise) {
    let statOk = false;
    try {
      const st = await fs.promises.stat(targetSessionParentPath);
      statOk = st.isDirectory();
      if (!statOk) {
        state.lastAcpStartError = `Agent cwd is not a directory: ${targetSessionParentPath}`;
        log(`[Agent] ${state.lastAcpStartError}`);
      }
    } catch {
      state.lastAcpStartError = `Agent cwd missing or inaccessible: ${targetSessionParentPath}`;
      log(`[Agent] ${state.lastAcpStartError}`);
    }
    if (!statOk) {
      return null;
    }

    const hooks = buildAcpSessionBridgeHooks({
      resolveRouting,
      sessionParentPath: targetSessionParentPath,
      getSendSessionUpdate: () => sendSessionUpdate,
      getSendRequest: () => sendRequest,
      log,
      getAutoApproveAcpPermissions: () =>
        getCliPermissionModeFromAgentConfig(state.latestAgentConfigForBridgeHooks) ===
        CLI_PERMISSION_MODE_DANGEROUS,
      resolveAcpPermissionRequest: (requestId, result) => {
        state.acpHandle?.resolveRequest?.(requestId, result);
      },
    });
    const persisted =
      cloudSessionId != null &&
      cloudSessionId !== '' &&
      preferredAgentType != null &&
      preferredAgentType !== ''
        ? readLocalAgentSessionFile(cloudSessionId)
        : null;
    const persistedAcpSessionId =
      persisted &&
      persisted.backendAgentType === preferredAgentType &&
      typeof persisted.acpSessionId === 'string' &&
      persisted.acpSessionId.trim() !== ''
        ? persisted.acpSessionId.trim()
        : null;

    state.activeSessionConfigOptions = Array.isArray(persisted?.configOptions) ? persisted.configOptions : null;

    const spawnEpoch = state.clientEpoch;
    state.acpStartPromise = resolved
      .createClient({
        command: resolved.command,
        sessionMode: mode,
        agentConfig: agentConfig ?? null,
        backendAgentType: preferredAgentType,
        persistedAcpSessionId,
        getActiveConfigOptions: () => state.activeSessionConfigOptions,
        onAcpSessionEstablished: (info) => {
          state.activeSessionConfigOptions = info.configOptions ?? state.activeSessionConfigOptions;
          if (
            cloudSessionId != null &&
            cloudSessionId !== '' &&
            preferredAgentType != null &&
            preferredAgentType !== ''
          ) {
            writeLocalAgentSessionFile(cloudSessionId, {
              acpSessionId: info.acpSessionId,
              configOptions: info.configOptions,
              backendAgentType: preferredAgentType,
            });
          }
          if (
            reportAgentCapabilities &&
            preferredAgentType &&
            Array.isArray(info.configOptions) &&
            info.configOptions.length > 0
          ) {
            reportAgentCapabilities({
              agentType: preferredAgentType,
              configOptions: info.configOptions,
            });
          }
        },
        onAcpConfigOptionsUpdated: (configOptions) => {
          state.activeSessionConfigOptions = configOptions;
          if (
            cloudSessionId != null &&
            cloudSessionId !== '' &&
            preferredAgentType != null &&
            preferredAgentType !== ''
          ) {
            writeLocalAgentSessionFile(cloudSessionId, {
              configOptions,
              backendAgentType: preferredAgentType,
            });
          }
          if (
            reportAgentCapabilities &&
            preferredAgentType &&
            Array.isArray(configOptions) &&
            configOptions.length > 0
          ) {
            reportAgentCapabilities({
              agentType: preferredAgentType,
              configOptions,
            });
          }
        },
        onAgentSubprocessExit: () => {
          if (state.acpHandle != null) {
            state.clientEpoch += 1;
          }
          state.acpHandle = null;
          state.acpStartPromise = null;
          state.acpAgentKey = null;
          state.activeSessionConfigOptions = null;
          state.latestAgentConfigForBridgeHooks = null;
          state.lastAcpStartError = 'Agent subprocess exited';
        },
        ...hooks,
        cwd: targetSessionParentPath,
        mcpServers,
      })
      .then(async (h) => {
        if (spawnEpoch !== state.clientEpoch || isCliImmediateShutdownRequested()) {
          try {
            await h.disconnectGracefully();
          } catch {
            /* ignore */
          }
          state.acpStartPromise = null;
          return null;
        }
        state.lastAcpStartError = null;
        state.acpHandle = h;
        state.lastAcpCwd = targetSessionParentPath;
        state.acpAgentKey = acpAgentKey;
        return h;
      })
      .catch((err) => {
        state.lastAcpStartError = errorMessage(err);
        log(`[Agent] Failed to start: ${state.lastAcpStartError}`);
        state.acpStartPromise = null;
        state.acpAgentKey = null;
        return null;
      });
  }

  return state.acpStartPromise;
}
